import { DocumentID } from '../common';
import { ImageSize } from '../posts';
import { SupportedSizeChartCategory } from '../providers';
import { SupportedMatchInfoHashCategory } from '../userMatchInfoHash';
import { Gender } from '../users/constants';
import { VisibilityLevel } from '../visibilityLevel';

export type ProductDocID = string;

export enum ProductGender {
  unknown = 0,
  unisex = 1,
  women = 2,
  men = 3,
  kids = 4,
}

export interface ImageURL {
  small: string;
  medium: string;
  large: string;
  full: string;
}
export interface ImageURLs {
  cutout: ImageURL | undefined;
  other: ImageURL[];
}

export const DefaultLanguage = 'en';
export type LanguageKey = string;
export type ProductName = string;
export type LocalizedNames = {
  [x: LanguageKey]: ProductName;
};

export enum SizeType {
  none = 0,
  default,
  custom,
  eu,
  fr,
  it,
  de,
  uk,
  us,
}

export interface ProductVariant {
  uid: string;
  type: SizeType;
  size?: string;
  sku?: string;
  index: number;
  inStock: boolean;
  otherOptions: { [key: string]: string };
}

export type ProductVariantUID = string;

export interface ProductVariantsMap {
  [x: ProductVariantUID]: ProductVariant[];
}

export interface ProductBase {
  documentID: string;
  uid: string;
  name: string;
  localizedNames: LocalizedNames;
  brandName: string;
  brandDocumentID: string;
  imageURLs: ImageURLs;
  providerDocumentID: string;
  gender: ProductGender;
  visibility: VisibilityLevel;
  url: string;
  variantUIDMap: ProductVariantsMap;
  matchingCategory: SupportedMatchInfoHashCategory;
  sizeChartCategory: SupportedSizeChartCategory;
  sizeCharts: DocumentID[];
  sizeRecAvailable: boolean;
}

export type ProductWidget = ProductBase;

export interface Product extends ProductBase {
  categoryDocumentIDs: DocumentID[];
  verifiedPosts: number; // Includes grouped product posts
  verifiedPostsWithBodyInfo: number; // Includes grouped product posts
  unmatchable?: boolean;
  imagePreviewPath?: string;
  collectionDocIDs: DocumentID[];
  productCategoryDocIDs: DocumentID[];
  productTypeDocIDs: DocumentID[];
  tagDocIDs: DocumentID[];
  dateAdded: Date;
  storeDateUpdated: Date;
}

export interface FullProduct extends Product {
  variantUIDs: ProductVariantUID[];
  variantSKUs: string[];
}

const productImageForSize = (imageURL: ImageURL | undefined, size: ImageSize) => {
  if (!imageURL) {
    return undefined;
  }
  switch (size) {
    case ImageSize.xxSmall:
    case ImageSize.xSmall:
    case ImageSize.small:
      // Fallback to medium if small not available
      return imageURL.small ?? imageURL.medium;
    case ImageSize.medium:
    case ImageSize.large:
      return imageURL.medium;
    case ImageSize.xLarge:
    case ImageSize.xxLarge:
      return imageURL.large;
    case ImageSize.original:
      return imageURL.full;
  }
  throw new Error(`Unhandled size ${size}`);
};

export const getProductImage = (product: ProductBase, size?: ImageSize): string => {
  const sizeToUse = size ?? ImageSize.large;
  let imageURL = productImageForSize(product.imageURLs.cutout, sizeToUse);
  if (!imageURL) {
    product.imageURLs.other.forEach((currentImageURL) => {
      if (imageURL) {
        return;
      }
      imageURL = productImageForSize(currentImageURL, sizeToUse);
    });
  }
  return imageURL ?? '';
};

export const getProductImageAlt = (product: ProductBase): string => {
  return product.name;
};

export const getUserGenderFromProduct = (product: ProductBase): Gender => {
  switch (product.gender) {
    case ProductGender.men:
      return Gender.male;
    case ProductGender.unisex:
    case ProductGender.women:
    case ProductGender.unknown:
      return Gender.female;
    case ProductGender.kids:
      return Gender.unset;
  }
  return Gender.female;
};

export const stringForProductGender = (gender: ProductGender): string => {
  switch (gender) {
    case ProductGender.men:
      return 'Men';
    case ProductGender.women:
      return 'Women';
    case ProductGender.unisex:
      return 'Unisex';
    case ProductGender.kids:
      return 'Kids';
    case ProductGender.unknown:
      return 'Unknown';
  }
  // skipcq JS-0378
  throw new Error(`Unknown gender ${gender}`);
};

export const longStringForProductGender = (gender: ProductGender): string => {
  switch (gender) {
    case ProductGender.men:
      return 'Menswear';
    case ProductGender.women:
      return 'Womenswear';
    case ProductGender.unisex:
      return 'Unisex';
    case ProductGender.kids:
      return 'Kidswear';
    case ProductGender.unknown:
      return 'Unknown';
  }
  // skipcq JS-0378
  throw new Error(`Unknown gender ${gender}`);
};

export interface SearchableProduct extends Product {
  queryNames: string[];
}

export const ProductsSearchInfoCollectionKey = 'productSearchInfo';

const beautyMap: { [key in SupportedMatchInfoHashCategory]: boolean } = {
  default: false,
  apparel: false,
  bras: false,
  beauty: true,
  skincare: true,
};
const apparelMap: { [key in SupportedMatchInfoHashCategory]: boolean } = {
  default: false,
  apparel: true,
  bras: true,
  beauty: false,
  skincare: false,
};

export const isBeautyMatchingCategory = (matchingCategory?: SupportedMatchInfoHashCategory) => {
  return !!matchingCategory && !!beautyMap[matchingCategory];
};

export const isApparelMatchingCategory = (matchingCategory?: SupportedMatchInfoHashCategory) => {
  return !!matchingCategory && !!apparelMap[matchingCategory];
};
